import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../api/initSupabase';
import {
  logErrorsWithMessage,
  logErrorsWithoutAlert,
} from '../../utils/errors';
import { localAnalytics } from '../../utils/analytics';
import { Loading } from '../common/Loading';
import { PaywallModalUnauthenticated } from '../PaywallModalUnauthenticated';
import { DiscountPaywallModal } from '../DiscountPaywallModal';
import { BookInProgressModal } from '../BookInProgressModal';
import { fbTracking } from '../../utils/fbTracking';
import {
  Header,
  BookDisplay,
  ChapterContent,
  ChapterBottomMenu,
  ChapterNavigation,
  ChapterList,
} from '.';
import {
  FontText,
  PrimaryButton,
  DownloadButtonsBanner,
  SEOHead,
} from '../common';
import { generateChapterSEO } from '../../utils/seoUtils';

interface ChapterType {
  id: number;
  book_id: number;
  number: number;
  title: string;
  is_premium: boolean;
  is_finished: boolean;
  coins: number;
  content?: string;
  preview?: string;
}

interface BookType {
  id: number;
  chapter_count: number;
  read_count: number;
  title: string;
  picture: string | null;
  author: string;
  slug: string;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords: string[];
  seo_alt?: string | null;
}

type FlowState = 'chapter' | 'paywall' | 'discount-paywall';

export const ChapterUnauthenticated: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { bookSlug, chapterNumber } = useParams<{
    bookSlug: string;
    chapterNumber: string;
  }>();
  const { isCampaign, isAnonymous } = useAuth();

  const [loading, setLoading] = useState(true);
  const [chapter, setChapter] = useState<ChapterType | null>(null);
  const [book, setBook] = useState<BookType | null>(null);
  const [chapterContent, setChapterContent] = useState<string | null>(null);
  const [showChapterList, setShowChapterList] = useState(false);
  const [allChapters, setAllChapters] = useState<ChapterType[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [fontSize, setFontSize] = useState(18);
  const [flowState, setFlowState] = useState<FlowState>('chapter');
  const [error, setError] = useState<string | null>(null);
  const [showDownloadButtons, setShowDownloadButtons] = useState(false);
  const [showInProgressModal, setShowInProgressModal] = useState(false);
  const [scrollToBannerTrigger, setScrollToBannerTrigger] = useState(0);
  React.useEffect(() => {
    const savedFontSize = localStorage.getItem('chapterFontSize');
    if (savedFontSize) {
      setFontSize(parseInt(savedFontSize, 10));
    }
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (flowState === 'paywall' || flowState === 'discount-paywall') {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopstate = () => {
      if (flowState === 'paywall' || flowState === 'discount-paywall') {
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    if (flowState === 'paywall' || flowState === 'discount-paywall') {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handlePopstate);
      window.history.pushState(null, '', window.location.pathname);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [flowState]);

  const handleFontSizeChange = (newSize: number) => {
    setFontSize(newSize);
    localStorage.setItem('chapterFontSize', newSize.toString());

    localAnalytics().logEvent('FontSizeChanged', {
      screen: 'ChapterUnauthenticated',
      action: 'FontSizeChanged',
      userId: null,
      fontSize: newSize,
    });
  };

  const fetchChapterData = async () => {
    if (!bookSlug || !chapterNumber) return;

    setLoading(true);
    setError(null);

    try {
      localAnalytics().logEvent('ChapterDataLoading', {
        screen: 'ChapterUnauthenticated',
        action: 'DataLoading',
        userId: null,
        bookSlug,
        chapterNumber,
      });

      const results = await Promise.all([
        supabase
          .from('book')
          .select(
            'id, chapter_count, title, picture, slug, read_count, seo_title, seo_description, seo_keywords, seo_alt, book_author!inner(author:author_id(name))'
          )
          .eq('slug', bookSlug)
          .single(),
        supabase
          .from('chapter')
          .select(
            `
            *,
            chapter_content (content),
            book!inner(slug)
          `
          )
          .eq('book.slug', bookSlug)
          .eq('number', parseInt(chapterNumber))
          .maybeSingle(),
        supabase
          .from('chapter')
          .select('*, book!inner(slug)')
          .eq('book.slug', bookSlug)
          .order('number'),
      ]);

      const [bookResult, chapterResult, allChaptersResult] = results;

      if (bookResult.error) throw bookResult.error;
      if (chapterResult.error) throw chapterResult.error;

      setBook({
        ...bookResult.data,
        author: bookResult.data.book_author[0].author.name,
      });
      setChapter(chapterResult.data);
      setAllChapters(allChaptersResult.data || []);

      if (bookResult.data && chapterResult.data) {
        fbTracking.trackViewContent(
          `${bookResult.data.title} - Chapter ${chapterNumber}`
        );
      }

      if (chapterResult.data) {
        const content = chapterResult.data.chapter_content?.[0]?.content;

        if (!chapterResult.data.is_finished) {
          setShowInProgressModal(true);
          return;
        }

        if (content && !chapterResult.data.is_premium) {
          setChapterContent(content);
        } else {
          setChapterContent('');
        }
      } else if (
        bookResult.data &&
        parseInt(chapterNumber || '1') <= bookResult.data.chapter_count
      ) {
        setShowInProgressModal(true);
        return;
      }

      localAnalytics().logEvent('ChapterDataLoaded', {
        screen: 'ChapterUnauthenticated',
        action: 'DataLoaded',
        userId: null,
        bookSlug,
        chapterNumber,
        hasContent: !!chapterResult.data?.chapter_content?.[0]?.content,
      });
    } catch (error) {
      console.error('Failed to load chapter:', error);
      setError('Failed to load chapter. Please try again.');
      logErrorsWithMessage(error, 'Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseSuccess = async (
    customerInfo: any,
    productId: string
  ) => {
    try {
      if (chapter?.id) {
        const { error } = await supabase.rpc('activate_chapter', {
          p_chapter_id: chapter?.id,
        });
        if (error) {
          logErrorsWithoutAlert(error, 'Error activating chapter');
        }
      }

      navigate('/purchase-finalize');
    } catch (error) {
      logErrorsWithMessage(error, 'Error handling purchase success');
      setError(
        'Purchase completed but there was an error. Please contact support.'
      );
    }
  };

  const handleBuyCoinsClick = () => {
    localAnalytics().logEvent('ChapterBuyCoinsClicked', {
      screen: 'ChapterUnauthenticated',
      action: 'BuyCoinsClicked',
      userId: null,
      bookSlug,
      chapterNumber,
      bookTitle: book?.title,
    });

    if (book) {
      fbTracking.trackAddToCart(book.title);
    }

    setFlowState('paywall');
  };

  const handlePaywallClose = () => {
    localAnalytics().logEvent('MainPaywallClosed', {
      screen: 'ChapterUnauthenticated',
      action: 'PaywallClosed',
      bookSlug,
      chapterNumber,
    });

    setFlowState('discount-paywall');
  };

  const handleDiscountPaywallClose = () => {
    localAnalytics().logEvent('DiscountPaywallClosed', {
      screen: 'ChapterUnauthenticated',
      action: 'DiscountPaywallClosed',
      bookSlug,
      chapterNumber,
    });

    setFlowState('chapter');
    setShowDownloadButtons(true);
    setScrollToBannerTrigger(prev => prev + 1);
  };

  const handleNextChapter = () => {
    if (!book || !chapterNumber) return;

    const nextChapter = parseInt(chapterNumber) + 1;

    if (nextChapter <= book.chapter_count) {
      localAnalytics().logEvent('ChapterNextClicked', {
        screen: 'ChapterUnauthenticated',
        action: 'NextClicked',
        userId: null,
        currentChapter: chapterNumber,
        nextChapter,
      });

      fbTracking.trackAddToWishlist(book.title);
    }
  };

  const handlePreviousChapter = () => {
    if (!book || !chapterNumber) return;

    const previousChapter = parseInt(chapterNumber) - 1;

    if (previousChapter >= 1) {
      localAnalytics().logEvent('ChapterPreviousClicked', {
        screen: 'ChapterUnauthenticated',
        action: 'PreviousClicked',
        userId: null,
        currentChapter: chapterNumber,
        previousChapter,
      });
    }
  };

  const handleChapterSelect = (selectedChapterNumber: number) => {
    localAnalytics().logEvent('ChapterSelected', {
      screen: 'ChapterUnauthenticated',
      action: 'ChapterSelected',
      userId: null,
      currentChapter: chapterNumber,
      selectedChapter: selectedChapterNumber,
    });

    setShowChapterList(false);
  };

  useEffect(() => {
    fetchChapterData();
  }, [bookSlug, chapterNumber]);

  useEffect(() => {
    if (!loading && !error && (!book || (!chapter && !showInProgressModal))) {
      localAnalytics().logEvent('ChapterNotFoundRedirect', {
        screen: 'ChapterUnauthenticated',
        action: 'RedirectTo404',
        bookSlug,
        chapterNumber,
        reason: !book ? 'book_not_found' : 'chapter_not_found',
      });
      navigate('/404', { replace: true });
    }
  }, [
    navigate,
    bookSlug,
    chapterNumber,
    book,
    chapter,
    showInProgressModal,
    loading,
    error,
  ]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    const styles: Record<string, React.CSSProperties> = {
      errorContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: theme.colors.background,
        padding: '20px',
      },
      errorText: {
        color: theme.colors.textSecondary,
        marginBottom: 20,
        textAlign: 'center',
      },
    };

    return (
      <div style={styles.errorContainer}>
        <FontText normal style={styles.errorText}>
          {error}
        </FontText>
        <PrimaryButton onClick={fetchChapterData}>{t('reload')}</PrimaryButton>
      </div>
    );
  }

  if (!book || (!chapter && !showInProgressModal)) {
    return null;
  }

  const hasNextChapter = parseInt(chapterNumber || '1') < book.chapter_count;
  const hasPreviousChapter = parseInt(chapterNumber || '1') > 1;
  const isContentLocked = !chapterContent;

  const seoData =
    book && chapter
      ? generateChapterSEO(
          book,
          {
            number: parseInt(chapterNumber || '1'),
            title: chapter.title || '',
            preview: chapter.preview,
          },
          t
        )
      : null;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      display: 'flex',
      flexDirection: 'column',
    },
    main: {
      flex: 1,
      overflowY: 'auto',
    },
  };

  return (
    <>
      {seoData && (
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          canonical={seoData.canonical}
          ogImage={seoData.ogImage}
          ogType={seoData.ogType}
        />
      )}
      <div style={styles.container}>
        <Header
          isPremium={false}
          isAuthenticated={false}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={0}
          menuOpen={menuOpen}
          onMenuToggle={() => setMenuOpen(!menuOpen)}
        />

        <main style={styles.main}>
          <BookDisplay
            book={book}
            chapter={{
              number: parseInt(chapterNumber || '1'),
              title: chapter?.title || '',
            }}
            readers={book.read_count}
            showStats={!isCampaign}
          />

          {showInProgressModal ? (
            <BookInProgressModal
              isOpen={showInProgressModal}
              bookId={book.id}
              bookTitle={book.title}
              bookSlug={book.slug}
              currentChapter={parseInt(chapterNumber || '1')}
              totalChapters={book.chapter_count}
              onClose={() => setShowInProgressModal(false)}
            />
          ) : (
            <>
              <ChapterContent
                content={chapterContent}
                preview={chapter?.preview}
                isLocked={isContentLocked}
                isAuthenticated={false}
                showUnlockPrompt={false}
                userCoins={0}
                chapterCoins={chapter?.coins}
                autoUnlockEnabled={false}
                onUnlock={() => {}}
                onBuyCoins={handleBuyCoinsClick}
                onAutoUnlockToggle={() => {}}
                fontSize={fontSize}
                chapter={{
                  number: parseInt(chapterNumber || '1'),
                  title: chapter?.title || '',
                }}
              />

              {chapterContent && (
                <ChapterNavigation
                  hasNextChapter={hasNextChapter}
                  hasPreviousChapter={hasPreviousChapter}
                  currentChapter={parseInt(chapterNumber || '1')}
                  totalChapters={book.chapter_count}
                  bookSlug={bookSlug!}
                  onNextChapter={handleNextChapter}
                  onPreviousChapter={handlePreviousChapter}
                />
              )}
            </>
          )}

          {showDownloadButtons && (
            <div style={{ marginBottom: 200, marginTop: -80 }}>
              <DownloadButtonsBanner
                bookTitle={book?.title}
                chapterNumber={parseInt(chapterNumber || '1')}
                source="discount_paywall_close"
                scrollTrigger={scrollToBannerTrigger}
              />
            </div>
          )}
        </main>

        <ChapterBottomMenu
          isAuthenticated={false}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          currentChapter={parseInt(chapterNumber || '1')}
          totalChapters={book.chapter_count}
          fontSize={fontSize}
          onShowChapterList={() => setShowChapterList(true)}
          onFontSizeChange={handleFontSizeChange}
          onAddToFavorites={() => {}}
          isFavorite={false}
        />

        <ChapterList
          isOpen={showChapterList}
          chapters={allChapters}
          currentChapter={parseInt(chapterNumber || '1')}
          bookTitle={book.title}
          bookSlug={bookSlug!}
          onClose={() => setShowChapterList(false)}
          onChapterSelect={handleChapterSelect}
        />

        {flowState === 'paywall' && (
          <PaywallModalUnauthenticated
            isOpen={true}
            onClose={handlePaywallClose}
            onPurchaseSuccess={handlePurchaseSuccess}
            bookTitle={book.title}
            chapterNumber={parseInt(chapterNumber || '1')}
          />
        )}

        {flowState === 'discount-paywall' && (
          <DiscountPaywallModal
            isOpen={true}
            onClose={handleDiscountPaywallClose}
            onPurchaseSuccess={handlePurchaseSuccess}
            bookTitle={book.title}
            chapterNumber={parseInt(chapterNumber || '1')}
          />
        )}
      </div>
    </>
  );
};
